import { LENDER_MODE_NO_MODE } from '../../../assets'
import { safeDivide } from './formatting'
import { getOraclePrice } from './oraclePrice'
import {
  buildUnderlyingInfo,
  getMarketUidsFromMeta,
  LenderCrossPoolMeta,
  UserData,
  UserDataForSubAccount,
} from './types'

/**
 * Euler sub-account position payload.
 * Similar to InitUserReserveResponse but keyed by numeric sub-account index
 * (e.g. "0", "1", "34") and using controller vault as the "mode".
 */
export interface EulerUserReserveResponse {
  chainId: string
  lendingPositions: {
    [subAccountIndex: string]: {
      [marketUid: string]: {
        marketUid: string
        underlying: string
        deposits: string
        debt: string
        debtStable: string
        depositsUSD: number
        debtUSD: number
        debtStableUSD: number
        depositsUSDOracle: number
        debtUSDOracle: number
        debtStableUSDOracle: number
        collateralEnabled: boolean
        isAllowed: boolean
      }
    }
  }
  /** Controller vault per sub-account index — serves as the "mode" for config lookups */
  controllers: { [subAccountIndex: string]: string }
  account: string
}

/**
 * Aggregates Euler V2 user data across sub-accounts into the standard UserData format.
 *
 * Sub-accounts are identified by their numeric index (e.g. "0", "1", "34"),
 * NOT by derived hex addresses.
 *
 * Euler's config model differs from INIT:
 * - INIT uses numeric mode IDs per position
 * - Euler uses the controller vault address as the config key
 *   (each vault's config maps counterparty vault → LTV params)
 *
 * For each sub-account, the controller vault address determines which
 * config entry to use when looking up collateral/borrow factors.
 */
export function createEulerMultiAccountTypeUserState(
  payload: EulerUserReserveResponse,
  lenderData: LenderCrossPoolMeta,
  histData: {
    [subAccountIndex: string]: {
      totalDebt24h: number
      totalDeposits24h: number
    }
  },
): UserData {
  const subAccountIndexes = Object.keys(payload.lendingPositions ?? {})
  const marketUids = getMarketUidsFromMeta(lenderData)
  const { chainId, account } = payload

  const data: UserDataForSubAccount[] = []

  for (let j = 0; j < subAccountIndexes.length; j++) {
    const subAccountIndex = subAccountIndexes[j]
    // controller vault address serves as both the mode and config lookup key
    const controller = payload.controllers?.[subAccountIndex]
    const configKey = controller ?? String(LENDER_MODE_NO_MODE)
    const userConfig = {
      id: subAccountIndex,
      selectedMode: configKey,
      isWhitelisted: true,
    }

    // organic yields
    let depositInterest = 0
    let borrowInterest = 0
    // rewards
    let rewardDepositAccrual = 0
    let rewardBorrowAccrual = 0
    // staking
    let stakingDepositAccrual = 0
    let stakingBorrowAccrual = 0
    // amounts
    let deposits = 0
    let debt = 0
    let collateral = 0
    let borrowDiscountedCollateral = 0
    let adjustedDebt = 0

    for (let i = 0; i < marketUids.length; i++) {
      const marketUid = marketUids[i]

      if (
        !lenderData?.[marketUid] ||
        !payload.lendingPositions[subAccountIndex][marketUid]
      )
        continue

      const pos = payload.lendingPositions[subAccountIndex][marketUid]
      const { depositsUSD, debtUSD } = pos
      const depositsUSDOracle = pos.depositsUSDOracle ?? depositsUSD
      const debtUSDOracle = pos.debtUSDOracle ?? debtUSD
      const {
        depositRate,
        intrinsicYield,
        variableBorrowRate,
        rewards,
        configs,
      } = lenderData[marketUid]

      // amounts (display)
      deposits += depositsUSD
      debt += debtUSD

      // rewards
      ;(rewards ?? []).forEach((rewardData: any) => {
        rewardDepositAccrual += rewardData.depositRate * depositsUSD
        rewardBorrowAccrual +=
          rewardData.variableBorrowRate * debtUSD +
          (rewardData.stableBorrowRate ?? 0) * debtUSD
      })

      // staking
      stakingDepositAccrual += (intrinsicYield ?? 0) * depositsUSD
      stakingBorrowAccrual += (intrinsicYield ?? 0) * debtUSD

      // config lookup: use controller vault address as key
      const config = configs?.[configKey]

      // risk adjusted (oracle-based for accuracy)
      collateral += (config?.collateralFactor ?? 1) * depositsUSDOracle
      borrowDiscountedCollateral +=
        (config?.borrowCollateralFactor ?? 1) * depositsUSDOracle
      adjustedDebt += (config?.borrowFactor ?? 1) * debtUSDOracle

      // IRs
      depositInterest += depositRate! * depositsUSD
      borrowInterest += debtUSD * variableBorrowRate!
    }

    const nav = deposits - debt

    // aggregated balance data
    const balanceData = {
      borrowDiscountedCollateral,
      borrowDiscountedCollateralAllActive: borrowDiscountedCollateral,
      collateral,
      collateralAllActive: collateral,
      deposits,
      debt,
      adjustedDebt,
      nav,
      deposits24h: histData[subAccountIndex]?.totalDeposits24h,
      debt24h: histData[subAccountIndex]?.totalDebt24h,
      nav24h:
        histData[subAccountIndex]?.totalDeposits24h -
        histData[subAccountIndex]?.totalDebt24h,
    }

    // aggregated apr data
    const aprData = {
      apr: safeDivide(depositInterest - borrowInterest, nav),
      borrowApr: safeDivide(borrowInterest, debt),
      depositApr: safeDivide(depositInterest, deposits),
      rewardApr: safeDivide(rewardDepositAccrual + rewardBorrowAccrual, nav),
      rewardDepositApr: safeDivide(rewardDepositAccrual, deposits),
      rewardBorrowApr: safeDivide(rewardBorrowAccrual, debt),
      intrinsicApr: safeDivide(
        stakingDepositAccrual - stakingBorrowAccrual,
        nav,
      ),
      intrinsicDepositApr: safeDivide(stakingDepositAccrual, deposits),
      intrinsicBorrowApr: safeDivide(stakingBorrowAccrual, debt),
      rewards: {},
    }

    // enrich positions with withdrawable/borrowable amounts
    const creditLine = Math.max(0, borrowDiscountedCollateral - adjustedDebt)
    for (let i = 0; i < marketUids.length; i++) {
      const marketUid = marketUids[i]
      if (!lenderData?.[marketUid]) continue

      const { configs, flags, borrowLiquidity, withdrawLiquidity } =
        lenderData[marketUid]
      const config = configs?.[configKey]
      const price = getOraclePrice(lenderData[marketUid])
      const bcf = config?.borrowCollateralFactor ?? 1
      const bf = config?.borrowFactor ?? 1
      const vaultAddr = (lenderData[marketUid]?.params?.metadata as any)
        ?.vault as string | undefined

      const pos = payload.lendingPositions[subAccountIndex]?.[marketUid]

      if (pos) {
        let withdrawable: string
        if (!pos.collateralEnabled || config?.collateralDisabled) {
          withdrawable = String(pos.deposits)
        } else if (balanceData.debt === 0) {
          withdrawable = String(pos.deposits)
        } else {
          const withdrawableUSD = Math.max(creditLine / bcf, 0)
          withdrawable = String(
            Math.min(withdrawableUSD / price, Number(pos.deposits)),
          )
        }
        if (withdrawLiquidity != null) {
          withdrawable = String(
            Math.min(Number(withdrawable), withdrawLiquidity),
          )
        }

        let borrowable: string
        if (
          !flags?.borrowingEnabled ||
          flags?.isFrozen ||
          config?.debtDisabled
        ) {
          borrowable = '0'
        } else if (debt > 0 && vaultAddr !== controller) {
          // user has debt: only the controller (debt) vault can increase borrowing
          borrowable = '0'
        } else {
          const borrowableUSD = Math.max(creditLine / bf, 0)
          borrowable = String(borrowableUSD / price)
        }
        if (borrowLiquidity != null) {
          borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
        }

        ;(pos as any).withdrawable = withdrawable
        ;(pos as any).borrowable = borrowable
        ;(pos as any).underlyingInfo = buildUnderlyingInfo(
          lenderData[marketUid],
        )
      } else if (
        (deposits > 0 || debt > 0) &&
        flags?.borrowingEnabled &&
        !flags?.isFrozen &&
        !config?.debtDisabled &&
        (debt === 0 || vaultAddr === controller)
      ) {
        let borrowable = String(Math.max(creditLine / bf, 0) / price)
        if (borrowLiquidity != null) {
          borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
        }

        payload.lendingPositions[subAccountIndex][marketUid] = {
          marketUid,
          underlying: lenderData[marketUid].underlying,
          deposits: '0',
          debt: '0',
          debtStable: '0',
          depositsUSD: 0,
          debtUSD: 0,
          debtStableUSD: 0,
          depositsUSDOracle: 0,
          debtUSDOracle: 0,
          debtStableUSDOracle: 0,
          collateralEnabled: false,
          isAllowed: false,
          withdrawable: '0',
          borrowable,
          underlyingInfo: buildUnderlyingInfo(lenderData[marketUid]),
        } as any
      }
    }

    data.push({
      accountId: subAccountIndex,
      health:
        balanceData.debt === 0
          ? null
          : balanceData.adjustedDebt > 0
            ? balanceData.collateral / balanceData.adjustedDebt
            : balanceData.collateral / balanceData.debt,
      borrowCapacityUSD: creditLine,
      userConfig,
      balanceData,
      aprData,
      positions: Object.values(
        payload.lendingPositions[subAccountIndex],
      ) as any,
    })
  }

  return {
    lender: '',
    account,
    chainId,
    data,
  }
}
